import { ParsedBusinessResult, parseBusinessEnvelope } from './parseResponse';
import { BusinessSubmissionRecovery } from './contract';
import { isValidCui } from './cui';

/** Project only the original accepted identity. No new lookup, key, details or credential comes from this value. */
export const parseSubmissionRecovery = (value: unknown): BusinessSubmissionRecovery | null => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const { idempotencyKey, business } = value as Record<string, unknown>;
    if (typeof idempotencyKey !== 'string' || !/^[A-Za-z0-9_-]{8,128}$/.test(idempotencyKey)
        || !business || typeof business !== 'object' || Array.isArray(business)) return null;
    const { cui, verificationReference } = business as Record<string, unknown>;
    if (typeof cui !== 'string' || !isValidCui(cui) || typeof verificationReference !== 'string'
        || !verificationReference.trim() || verificationReference.length > 128) return null;
    return { idempotencyKey, business: { cui, verificationReference } };
};

/**
 * The attempt BOLT reports with the session (`businessState`), read exactly like a live answer. `null` (no attempt) and
 * anything that cannot be read are the same: nothing to restore. It never grants anything by itself.
 */
export const parseRestoredState = (businessState: unknown): ParsedBusinessResult | null => {
    if (businessState === null || businessState === undefined) {
        return null;
    }
    const parsed = parseBusinessEnvelope(businessState);
    return parsed.kind === 'unknown' ? null : parsed;
};

/**
 * A failed session request (HTTP 401/403/410...) carries the same envelope in its body. Only a failure counts: any
 * other rejection (network, 5xx without an envelope) restores nothing.
 */
export const sessionErrorState = (error: unknown): ParsedBusinessResult | null => {
    const body = (error as { response?: { data?: unknown } } | null | undefined)?.response?.data;
    if (body === undefined || body === null) {
        return null;
    }
    const parsed = parseBusinessEnvelope(body);
    return parsed.kind === 'failure' ? parsed : null;
};
